import React, { useState, useCallback } from 'react';
import { useCanvasAnimation } from '../useCanvasAnimation';

interface NebulaCollapseProps {
  width?: number | string;
  height?: number | string;
}

const NebulaFallbackCanvas: React.FC<{ size: number }> = ({ size }) => {
  const draw = useCallback((ctx: CanvasRenderingContext2D, frameCount: number) => {
    ctx.fillStyle = '#0F172A';
    ctx.fillRect(0, 0, size, size);

    const centerX = size / 2;
    const centerY = size / 2;
    
    const time = (frameCount % 300) / 300; // 0 to 1 loop (collapse)
    
    const cloudRadius = 150 - time * 120; // Shrinks from 150 to 30
    const cloudAlpha = 0.3 + time * 0.5; // Gets denser
    
    // Draw Nebula Cloud
    ctx.save();
    const gradient = ctx.createRadialGradient(centerX, centerY, 5, centerX, centerY, cloudRadius);
    gradient.addColorStop(0, `rgba(167, 139, 250, ${cloudAlpha})`); // Purple
    gradient.addColorStop(0.5, `rgba(56, 189, 248, ${cloudAlpha * 0.5})`); // Blue
    gradient.addColorStop(1, 'rgba(15, 23, 42, 0)');
    
    ctx.fillStyle = gradient;
    
    // Spin it
    ctx.translate(centerX, centerY);
    ctx.rotate(frameCount * 0.01 * (1 + time * 5)); // Spins faster as it collapses
    
    ctx.beginPath();
    ctx.ellipse(0, 0, cloudRadius, cloudRadius * 0.7, 0, 0, Math.PI * 2);
    ctx.fill();
    ctx.restore();

    // Central protostar starts glowing
    if (time > 0.5) {
      const starAlpha = (time - 0.5) * 2;
      ctx.beginPath();
      ctx.fillStyle = `rgba(253, 224, 71, ${starAlpha})`;
      ctx.arc(centerX, centerY, 10 + time * 10, 0, Math.PI * 2);
      ctx.fill();
    }

  }, [size]);

  const canvasRef = useCanvasAnimation(draw, size);

  return <canvas ref={canvasRef} style={{ borderRadius: '12px', width: '100%', height: '100%' }} />;
};

export const NebulaCollapse: React.FC<NebulaCollapseProps> = ({ width = '100%', height = 400 }) => {
  const [hasError, setHasError] = useState(false);
  const [showIframe, setShowIframe] = useState(true);

  return (
    <div style={{ width, height, position: 'relative', display: 'flex', justifyContent: 'center', alignItems: 'center', background: '#0F172A', borderRadius: '12px', overflow: 'hidden' }}>
      {showIframe && !hasError ? (
        <iframe
          width="100%"
          height="100%"
          src="https://www.youtube.com/embed/Ywdk_g1E3nE?autoplay=1&mute=1&loop=1&playlist=Ywdk_g1E3nE&controls=0&modestbranding=1"
          title="Nebula Collapse"
          frameBorder="0"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
          onError={() => setHasError(true)}
          style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%' }}
        />
      ) : (
        <NebulaFallbackCanvas size={Number(height)} />
      )}
      
      {!hasError && showIframe && (
        <div style={{ position: 'absolute', bottom: 10, right: 10, zIndex: 10 }}>
          <button 
            onClick={() => setShowIframe(false)}
            style={{ background: 'rgba(0,0,0,0.5)', color: 'white', border: 'none', padding: '4px 8px', borderRadius: '4px', fontSize: '10px', cursor: 'pointer' }}>
            Switch to 2D Fallback
          </button>
        </div>
      )}
    </div>
  );
};
